import { MouseEvent, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Check, ContentCopy, ErrorOutlineRounded, WarningAmberRounded } from '@mui/icons-material';
import {
  CopyButton,
  IssueSeverity,
  MessageCard,
  MessageList,
  MessageText,
  TooltipRoot,
} from './NodeIssueTooltip.styles';

type Props = {
  severity: IssueSeverity;
  title: string;
  messages: string[];
};

export const NodeIssueTooltip = ({ severity, title, messages }: Props) => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const color = severity === 'error' ? 'error.light' : 'warning.light';
  const Icon = severity === 'error' ? ErrorOutlineRounded : WarningAmberRounded;
  const multiple = messages.length > 1;

  const handleCopy = (event: MouseEvent, message: string, index: number) => {
    event.stopPropagation();
    void navigator.clipboard?.writeText(message);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex((current) => (current === index ? null : current)), 1500);
  };

  return (
    <TooltipRoot>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: messages.length ? 0.75 : 0 }}>
        <Icon sx={{ fontSize: 16, color }} />
        <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.02em', color }}>
          {title}
          {multiple && (
            <Box component="span" sx={{ ml: 0.75, opacity: 0.7, fontWeight: 500 }}>
              ({messages.length})
            </Box>
          )}
        </Typography>
      </Box>
      <MessageList>
        {messages.map((message, index) => (
          <MessageCard key={index} severity={severity}>
            <MessageText variant="body2">{message}</MessageText>
            <CopyButton
              size="small"
              severity={severity}
              copied={copiedIndex === index}
              aria-label="Copy message"
              onClick={(event) => handleCopy(event, message, index)}
            >
              {copiedIndex === index ? <Check sx={{ fontSize: 13 }} /> : <ContentCopy sx={{ fontSize: 12 }} />}
            </CopyButton>
          </MessageCard>
        ))}
      </MessageList>
    </TooltipRoot>
  );
};

export default NodeIssueTooltip;
